import { createFileRoute, Outlet } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { type TabItem, TabsBar } from '@/common/TabsBar';
import { useCurrentUser } from '~/hooks/useCurrentUser';

/**
 * The coordinator's and the partner's follow-up portal, UN-GROUPED.
 *
 * Legacy hangs these two tabs off a BuddyPress group extension —
 * `class-follow-up-coordinator-group-follow-up.php:19-33` and
 * `class-follow-up-partner-group-follow-up.php:19-33`, each returning exactly
 * `{pending-requests, entries}` in that order. There is no group to hang them
 * on here: W4.2 retired both follow-up `GroupType` members, all 84 surviving
 * follow-up groups carry `group_type` NULL and zero `group_memberships` rows,
 * and `group_memberships.user_id` FKs `study_helpers(id)` so the roster is
 * unreproducible by construction. Both portals are therefore mounted at
 * `/follow-ups/*` and scoped by the caller's own column.
 *
 * THIS SHELL CARRIES NO PROFILE-TYPE GATE, DELIBERATELY, and the reason is not
 * that its children are ungated — three of the five carry one of their own.
 * It is the parent of `follow-ups/entries/view/{id}`, whose server gate admits
 * the student, the student's current study helper and an admin as well as the
 * two follow-up roles, and every one of those reaches the detail from a View
 * action on a table this group restored. A `FOLLOWUP_COORDINATOR ||
 * FOLLOWUP_PARTNER` test here would bounce all three onto NotFoundScreen. The
 * per-row viewer set is not expressible as a profile type, so the detail's gate
 * is its endpoint's 404 and this shell adds nothing to it.
 *
 * The SUBNAV is gated, which is a different question: a study helper who opened
 * a detail sees the page, not a tab strip whose two destinations both refuse
 * them.
 */
function useFollowUpTabs(): TabItem[] {
  const { t } = useTranslation();
  return [
    { title: t('wbs.pending-requests'), to: 'pending-requests' },
    { title: t('wbs.follow-ups'), to: 'entries' },
  ];
}

function FollowUpsLayout(): ReactElement {
  const tabs = useFollowUpTabs();
  const { me, isPending } = useCurrentUser();

  // `me.profile_type`, not `useCurrentUser().isStudyHelper`, which folds
  // FOLLOWUP_COORDINATOR, FOLLOWUP_PARTNER, STUDY_HELPER and ADMIN together
  // (`hooks/useCurrentUser.ts:14-19`), and not `useIsCoordinator`, which reads
  // `params.groupName` and disables its query without one — on a top-level
  // route its `isResolved` never flips.
  const hasSubnav =
    !isPending &&
    (me?.profile_type === 'FOLLOWUP_COORDINATOR' ||
      me?.profile_type === 'FOLLOWUP_PARTNER');

  return (
    <>
      {hasSubnav && <TabsBar tabs={tabs} base="/follow-ups" />}
      <Outlet />
    </>
  );
}

export const Route = createFileRoute('/_app/follow-ups')({
  component: FollowUpsLayout,
});
